import { useToast } from "@chakra-ui/react";
import { UseQueryResult, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";

import {
  SearchStateAccept,
  SearchStateReturn,
  SearchValue,
  SetStateOptions,
} from "@nens/react/hooks";
import { OurUseQueryOptions } from "@nens/react-query-utils";

import { ExtraOptions, useRanaSearchState } from "./router/rana-search-state";

const EMPTY_STRING = "";

export type SearchStateSetter<T extends SearchValue | SearchValue[]> = SearchStateReturn<T>[1];

type QuerySearchStateReturn<T extends { id: string | number }> = {
  data: T | undefined;
  setData: (object: T | undefined, options?: SetStateOptions) => void;
  setDataId: SearchStateSetter<T["id"] | undefined>;
} & UseQueryResult<T, unknown>;

type StringOptions = ExtraOptions & { idIsNumber?: false };
type NumberOptions = ExtraOptions & { idIsNumber: true };

// This code saves the id of a query in the search state and then uses react-query to get the whole item.
// On setting the data it will set the id in the search state and the query data.
// IMPORTANT: Item must contain an id
export function useQuerySearchState<T extends { id: string }>(
  key: string,
  getQuery: (id?: T["id"]) => OurUseQueryOptions<T>,
  options?: StringOptions
): QuerySearchStateReturn<T>;

export function useQuerySearchState<T extends { id: number }>(
  key: string,
  getQuery: (id?: T["id"]) => OurUseQueryOptions<T>,
  options: NumberOptions
): QuerySearchStateReturn<T>;

export function useQuerySearchState<T extends { id: string | number }>(
  key: string,
  getQuery: (id?: T["id"]) => OurUseQueryOptions<T>,
  options?: StringOptions | NumberOptions
): QuerySearchStateReturn<T> {
  type DataId = T["id"] | undefined;
  const intl = useIntl();
  const toast = useToast();
  const queryClient = useQueryClient();

  const type = options?.idIsNumber ? "number" : "string";

  const [dataId, setDataId] = useRanaSearchState<DataId>(
    key,
    undefined,
    [type, "undefined"] as SearchStateAccept<DataId>[],
    options
  );

  const queryOptions = getQuery(dataId);
  const query = useQuery({
    ...queryOptions,
    queryFn: async () => {
      try {
        const { queryFn } = queryOptions;
        if (typeof queryFn !== "function") throw Error("queryFn is not a function");
        return await queryFn();
      } catch (e) {
        setDataId(undefined);

        toast({
          status: "error",
          title: intl.formatMessage({
            id: "error",
            defaultMessage: "Error!",
          }),
          description: intl.formatMessage(
            {
              id: "error.could_not_find",
              defaultMessage: "Could not find this {key}",
            },
            { key: key === EMPTY_STRING ? "item" : key }
          ),
        });
        throw e;
      }
    },
  });

  const setData = (object: T | undefined, options?: SetStateOptions) => {
    // We have to use an assertion here because of typescript overload resolution issues.
    type SetDataIdFirstArg = Parameters<typeof setDataId>[0];
    setDataId(object?.id as SetDataIdFirstArg, options);
    queryClient.setQueryData(getQuery(object?.id).queryKey, object);
  };

  return { ...query, setData, setDataId };
}

// Using the useQuerySearchState hook with an empty string and disabling setting params,
// Will convert the search state into a normal state that does not care about the search params.
export const useQueryState = <T extends { id: string }>(
  getQuery: (id?: string) => OurUseQueryOptions<T>
) => {
  return useQuerySearchState(EMPTY_STRING, getQuery, {
    disableSettingParams: true,
  });
};
